import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { MetadataFieldList } from 'features/knowledgeDocumentViewer/components/metadata/MetadataFieldList'
import { getInternalDocumentIdDisplayFields } from 'features/knowledgeDocumentViewer/utils/documentMetadataFields'
import { documentTypeLabel } from 'features/knowledgeDocumentViewer/utils/documentTypeLabels'
import { formatDrawingMetadata } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const SectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

type DocumentDetailsField = {
  label: MessageDescriptor
  getValue: (document: DocumentTableRow) => string | null
  isDocumentRevision?: true
  formatValue?: (
    value: string,
    translate: (descriptor: MessageDescriptor) => string
  ) => string
}

const DOCUMENT_DETAILS_FIELDS: readonly DocumentDetailsField[] = [
  {
    label: msg`Type`,
    getValue: (document) => document.docType,
    formatValue: documentTypeLabel,
  },
  { label: msg`File Type`, getValue: (document) => document.fileType },
  {
    label: msg`Revision Code`,
    getValue: (document) => document.revisionCode,
    isDocumentRevision: true,
  },
  {
    label: msg`Revision Date`,
    getValue: (document) => document.revisionDate,
    isDocumentRevision: true,
  },
  {
    label: msg`Drawing #`,
    getValue: (document) =>
      formatDrawingMetadata(document.drawing, document.sheetNumber),
  },
  { label: msg`Vendor`, getValue: (document) => document.vendor },
  { label: msg`System`, getValue: (document) => document.system },
  {
    label: msg`Pages`,
    getValue: (document) =>
      document.numPages !== null && document.numPages > 0
        ? String(document.numPages)
        : null,
  },
]

export function hasPreviewDocumentDetails(
  document: DocumentTableRow,
  includeInternalIds = false
): boolean {
  return (
    DOCUMENT_DETAILS_FIELDS.some(({ getValue }) =>
      Boolean(getValue(document))
    ) ||
    (includeInternalIds &&
      getInternalDocumentIdDisplayFields({
        systemDocumentEntryId: document.systemEntryId,
        cantileverDocumentEntryId: document.cantileverDocumentEntryId,
      }).length > 0)
  )
}

export type PreviewDocumentDetailsSectionProps = {
  document: DocumentTableRow
  includeInternalIds?: boolean
  hideRevision?: boolean
}

export function PreviewDocumentDetailsSection({
  document,
  includeInternalIds = false,
  hideRevision = false,
}: PreviewDocumentDetailsSectionProps): React.ReactElement | null {
  const { i18n } = useLingui()
  const fields = useMemo(() => {
    const visible = DOCUMENT_DETAILS_FIELDS.flatMap(
      ({ label, getValue, formatValue, isDocumentRevision }) => {
        const value = getValue(document)
        if (!value || (hideRevision && isDocumentRevision)) return []
        return [
          {
            label: i18n._(label),
            value: formatValue
              ? formatValue(value, (descriptor) => i18n._(descriptor))
              : value,
          },
        ]
      }
    )
    if (!includeInternalIds) return visible
    return [
      ...visible,
      ...getInternalDocumentIdDisplayFields({
        systemDocumentEntryId: document.systemEntryId,
        cantileverDocumentEntryId: document.cantileverDocumentEntryId,
      }),
    ]
  }, [document, hideRevision, i18n, includeInternalIds])

  if (fields.length === 0) return null

  return (
    <SectionRoot>
      <Text as='h3' variant='body' weight='semibold'>
        <Trans>Document Details</Trans>
      </Text>
      <MetadataFieldList fields={fields} />
    </SectionRoot>
  )
}
